import { catchError, mergeMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';
import { EntityBaseState } from '../base/entity.base.state';

export const COPYRIGHT_ENTITY_TOKEN = new StateToken<CopyrightEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.COPYRIGHT_ENTITY);

export type CopyrightEntityStateModel = FlatModels.EntityStatesModels.EntityStateModel<FlatModels.CopyrightEntityModels.Copyright>;

@State<CopyrightEntityStateModel>({
  name: COPYRIGHT_ENTITY_TOKEN,
  defaults: {
    entities: {},
  }
})
@Injectable()
export class CopyrightEntityState extends EntityBaseState<FlatModels.CopyrightEntityModels.Copyright, CopyrightEntityStateModel>{
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
    super();
  }

  //<editor-fold desc="Selectors">

  @Selector([CopyrightEntityState])
  static override typedEntities$(state: CopyrightEntityStateModel): { [id: string]: FlatModels.CopyrightEntityModels.Copyright } {
    return EntityBaseState.typedEntities$(state);
  }

  @Selector([CopyrightEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.CopyrightEntityModels.Copyright }): FlatModels.CopyrightEntityModels.Copyright[] {
    return EntityBaseState.entities$(typedEntities);
  }

  @Selector([CopyrightEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.CopyrightEntityModels.Copyright }) {
    return EntityBaseState.entity$(typedEntities);
  }

  @Selector([CopyrightEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.CopyrightEntityModels.Copyright }) {
    return EntityBaseState.entitiesByIds$(typedEntities);
  }

  @Selector([
    CopyrightEntityState.typedEntities$,
    FlatStates.MediaEntityState.allCurrentSelectedSlideMedias$
  ])
  static allCurrentSelectedSlideCopyrights$(typedEntities: { [id: string]: FlatModels.CopyrightEntityModels.Copyright },
                                            allCurrentSelectedSlideMedias: FlatModels.MediaEntityModels.MediaEntityType[]) {
    const medias = Array.isArray(allCurrentSelectedSlideMedias) ? allCurrentSelectedSlideMedias : [];

    const copyrights = medias.flatMap((media) => {
      return (media.mediaCopyrights || []).map(mc => ({ id: mc.copyright, text: mc.text, order: mc.order, copyright: typedEntities[mc.copyright] }));
    })

    const copyrightDedup = Array.from(new Map(copyrights.map((c) => [c.id + c.text, c])).values()).sort((a, b) => a.order - b.order);
    return copyrightDedup;
  }
  
  @Selector([CopyrightEntityState.entities$])
  static copyrightArray$(entities: FlatModels.CopyrightEntityModels.Copyright[]): {name: string, value: string}[] {
    return entities.reduce((arr, copyright) => {
      arr.push({
        value: copyright.id,
        name: copyright.text
      });
      return arr;
    }, [] as { value: string, name: string }[]);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.CopyrightEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<CopyrightEntityStateModel>,
                            action: FlatActions.CopyrightEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.CopyrightEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<CopyrightEntityStateModel>,
                              action: FlatActions.CopyrightEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.CopyrightEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<CopyrightEntityStateModel>,
                               action: FlatActions.CopyrightEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.CopyrightEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<CopyrightEntityStateModel>,
                                 action: FlatActions.CopyrightEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.CopyrightEntityActions.AddEntity)
  addEntity(_: StateContext<CopyrightEntityStateModel>,
            {entity}: FlatActions.CopyrightEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.CopyrightEntityState.addEntity"
      );
    }

    // Add Copyright
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.COPYRIGHT_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  @Action(FlatActions.CopyrightEntityActions.UpdateEntity)
  updateEntity(_: StateContext<CopyrightEntityStateModel>,
               {entity}: FlatActions.CopyrightEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.CopyrightEntityState.updateEntity"
      );
    }

    // Update Copyright
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.COPYRIGHT_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  @Action(FlatActions.CopyrightEntityActions.RemoveEntity)
  removeEntity(_: StateContext<CopyrightEntityStateModel>,
               {entity}: FlatActions.CopyrightEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.CopyrightEntityState.removeEntity"
      );
    }

    // Remove Copyright
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.COPYRIGHT_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
